'use client'

import { createPortal } from 'react-dom'
import { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/solid'
import useModalEffects from './useModalEffects'
import ModalHeader from './ModalHeader'

/**
 * Modal Component
 * Generic reusable modal with empire theme styling and comprehensive accessibility
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal closes
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} [props.title] - Optional built-in header title
 * @param {boolean} [props.showCloseButton=true] - Show X button
 * @param {boolean} [props.closeOnBackdropClick=true] - Close on backdrop click
 * @param {boolean} [props.closeOnEscape=true] - Close on ESC key
 * @param {boolean} [props.preventBodyScroll=true] - Lock body scroll when open
 * @param {string} [props.className=''] - Custom content classes
 * @param {string} [props.maxWidth='max-w-lg'] - Modal max width
 * @param {string} [props.overlayClassName=''] - Custom backdrop classes
 *
 * @example
 * // Basic usage
 * <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
 *   <div className="p-6">Content here</div>
 * </Modal>
 *
 * @example
 * // With built-in header
 * <Modal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Select Date & Time"
 * >
 *   <div className="p-6">Content here</div>
 * </Modal>
 */
const Modal = ({
  isOpen,
  onClose,
  children,
  title,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  preventBodyScroll = true,
  className = '',
  maxWidth = 'max-w-lg',
  overlayClassName = ''
}) => {
  const [mounted, setMounted] = useState(false)

  // Custom hook handles scroll lock, ESC key, focus trap
  useModalEffects({ isOpen, onClose, closeOnEscape, preventBodyScroll })

  // Ensure modal only renders on client
  useEffect(() => {
    setMounted(true)
  }, [])

  // Don't render until mounted or if not open
  if (!mounted || !isOpen) return null

  const handleBackdropClick = () => {
    if (closeOnBackdropClick) {
      onClose()
    }
  }

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] backdrop-blur-sm flex items-center justify-center p-4 max-sm:p-2 ${overlayClassName}`}
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 transition-opacity duration-300"
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        onClick={(e) => e.stopPropagation()}
        className={`
          relative bg-white rounded-lg shadow-2xl
          border border-secondary/20
          w-full ${maxWidth}
          max-h-[90vh] flex flex-col
          transform transition-all duration-300
          ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
          ${className}
        `}
      >
        {/* Optional Header */}
        {title && (
          <ModalHeader
            title={title}
            onClose={onClose}
            showCloseButton={showCloseButton}
          />
        )}

        {/* Close button (if no title but showCloseButton) */}
        {!title && showCloseButton && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100 z-10"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        )}

        {/* Content - Scrollable */}
        <div className={`overflow-y-auto flex-1 modal-scrollbar ${title ? '' : 'pt-4'}`}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default Modal
